'use client';

import { useToolUsageReport } from '@/components/tools/use-tool-usage-report';
import { useCallback, useEffect, useState } from 'react';
import { generateTotp, parseOtpauth, type TotpAlgorithm } from '@/lib/browser-2fa';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

export default function BrowserTwoFactorCodeGenerator() {
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [secondsRemaining, setSecondsRemaining] = useState(30);
  const [period, setPeriod] = useState(30);
  const [digits, setDigits] = useState(6);
  const [algorithm, setAlgorithm] = useState<TotpAlgorithm>('SHA-1');
  const [label, setLabel] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  // Usage is credited on the first code produced from a real secret, never on
  // mount: this tool renders empty, so a render is not usage.
  const [codesProduced, setCodesProduced] = useState(0);

  useToolUsageReport('2fa-code-generator', codesProduced > 0);

  const refresh = useCallback(async () => {
    if (!secret.trim()) {
      setCode('');
      setErrorMessage(null);
      return;
    }

    try {
      const result = await generateTotp({ secret, digits, period, algorithm });
      setCode(result.code);
      setSecondsRemaining(result.secondsRemaining);
      setErrorMessage(null);
      setCodesProduced((count) => (count === 0 ? 1 : count));
    } catch (error) {
      setCode('');
      setErrorMessage(error instanceof Error ? error.message : 'Could not generate a code.');
    }
  }, [secret, digits, period, algorithm]);

  /* One second is the right resolution: the countdown is the whole reason people
     trust the number on screen enough to type it into their bank.

     The interval restarts whenever a setting changes, which is deliberate. The
     first version kept the settings in a ref so the timer never restarted, and
     that reads a ref during render, which React forbids for a real reason: it
     is the shape that tears under concurrent rendering. A restarted one-second
     timer costs nothing. */
  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), 1000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  /* Pasting the whole otpauth:// link is the common case, because that is what
     the QR code on the setup page contains and what a QR reader hands back. It
     carries the digits, period and algorithm too, so accepting it removes three
     settings the person would otherwise have to match by hand. */
  function handleSecretChange(value: string) {
    setCopyMessage(null);

    if (/^otpauth:\/\//i.test(value.trim())) {
      try {
        const parts = parseOtpauth(value);
        setSecret(parts.secret);
        setDigits(parts.digits);
        setPeriod(parts.period);
        setAlgorithm(parts.algorithm);
        setLabel([parts.issuer, parts.account].filter(Boolean).join(' · '));
        setErrorMessage(null);
        return;
      } catch (error) {
        setSecret(value);
        setErrorMessage(error instanceof Error ? error.message : 'Could not read that link.');
        return;
      }
    }

    setLabel('');
    setSecret(value);
  }

  async function handleCopy() {
    if (!code) return;
    try {
      await copyText(code);
      setCopyMessage('Code copied.');
    } catch {
      setCopyMessage('The browser could not copy the code.');
    }
  }

  const progress = Math.max(0, Math.min(100, (secondsRemaining / period) * 100));

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">2FA</span>
        <h2 className="tool-live-title">Generate a 2FA code from your secret key</h2>
        <p className="tool-live-subtitle">
          The code is computed in this tab with the Web Crypto API. Your secret key is never sent
          anywhere, never stored, and disappears when you close the page.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>Secret key or otpauth:// link</span>
              <input
                autoComplete="off"
                onChange={(event) => handleSecretChange(event.target.value)}
                placeholder="JBSW Y3DP EHPK 3PXP"
                spellCheck={false}
                type="text"
                value={secret}
              />
            </label>

            <label className="tool-live-field">
              <span>Digits</span>
              <select onChange={(event) => setDigits(Number(event.target.value))} value={digits}>
                <option value={6}>6</option>
                <option value={7}>7</option>
                <option value={8}>8</option>
              </select>
            </label>

            <label className="tool-live-field">
              <span>Period (seconds)</span>
              <select onChange={(event) => setPeriod(Number(event.target.value))} value={period}>
                <option value={30}>30</option>
                <option value={60}>60</option>
              </select>
            </label>

            <label className="tool-live-field">
              <span>Algorithm</span>
              <select
                onChange={(event) => setAlgorithm(event.target.value as TotpAlgorithm)}
                value={algorithm}
              >
                <option value="SHA-1">SHA-1</option>
                <option value="SHA-256">SHA-256</option>
                <option value="SHA-512">SHA-512</option>
              </select>
            </label>
          </div>

          {label ? <p className="tool-live-panel-note">Account: {label}</p> : null}
          {errorMessage ? <p className="status-message status-error">{errorMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          {code ? (
            <>
              <p
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'clamp(2rem, 8vw, 3rem)',
                  fontWeight: 700,
                  letterSpacing: '0.18em',
                  margin: 0,
                }}
              >
                {code}
              </p>
              <div
                aria-label={`${secondsRemaining} seconds until this code expires`}
                aria-valuemax={period}
                aria-valuemin={0}
                aria-valuenow={secondsRemaining}
                role="progressbar"
                style={{
                  background: 'var(--line)',
                  borderRadius: '999px',
                  height: '4px',
                  margin: '0.75rem 0',
                  overflow: 'hidden',
                  width: '100%',
                }}
              >
                <div
                  style={{
                    background: 'var(--accent)',
                    height: '100%',
                    transition: 'width 1s linear',
                    width: `${progress}%`,
                  }}
                />
              </div>
              <p className="tool-live-panel-note">
                New code in {secondsRemaining}s. Codes stay valid for a short window after they
                change, so type it promptly.
              </p>
              <div className="tool-live-actions">
                <button className="button button-secondary" onClick={handleCopy} type="button">
                  Copy code
                </button>
              </div>
              {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
            </>
          ) : (
            <p className="tool-live-panel-note">
              Paste the secret key your service showed you when you set up two-factor
              authentication, or the full otpauth:// link behind its QR code.
            </p>
          )}
        </section>
      </div>
    </div>
  );
}
